import { fromZonedTime } from "date-fns-tz";
import { z } from "zod";

/**
 * The weekly expert check-in's answer vocabulary, shared by the handbook card,
 * the one-click email and the internal dashboard so every surface counts the
 * same answers.
 */

export const CHECK_IN_RATINGS = [1, 2, 3, 4, 5] as const;

export const CHECK_IN_RATING_LABELS: Record<number, string> = {
  1: "Rough",
  2: "Not great",
  3: "Okay",
  4: "Good",
  5: "Great",
};

/**
 * The two 1-5 questions, each with the hint under it. The Terac one is the
 * headline: the only question the email asks, and the card's first. The
 * project one is asked on the card only, and never names the client.
 */
export const CHECK_IN_RATING_QUESTIONS = {
  teracRating: {
    question: "How was working with Terac this week?",
    hint: "Your project lead, support and our platform",
  },
  projectRating: {
    question: "How was this project's work and tools this week?",
    hint: "The tasks, the instructions and the client's platform",
  },
} as const;

export const CHECK_IN_FULFILLMENTS = ["YES", "SOMEWHAT", "NO"] as const;
export type CheckInFulfillment = (typeof CHECK_IN_FULFILLMENTS)[number];

export const CHECK_IN_FULFILLMENT_LABELS: Record<CheckInFulfillment, string> = {
  YES: "Yes",
  SOMEWHAT: "Somewhat",
  NO: "Not really",
};

export const CHECK_IN_OUTLOOKS = ["MORE", "SAME", "LESS", "STOPPING"] as const;
export type CheckInOutlook = (typeof CHECK_IN_OUTLOOKS)[number];

export const CHECK_IN_OUTLOOK_LABELS: Record<CheckInOutlook, string> = {
  MORE: "More hours",
  SAME: "About the same",
  LESS: "Fewer hours",
  STOPPING: "I'm planning to stop",
};

/**
 * What an expert can say they want more of. Drawn from the kinds of project
 * work the platform actually runs, so demand generation can read the counts as
 * "go find more of this". `MORE_OF_THIS` is the strongest signal of all: the
 * current project, as it is.
 */
export const CHECK_IN_WORK_TYPES = [
  "MORE_OF_THIS",
  "RL_ENVIRONMENTS",
  "AI_EVALUATION",
  "EXPERT_TASKS",
  "CODING",
  "RED_TEAMING",
  "DESIGN_CREATIVE",
  "VIDEO_RECORDING",
  "ONGOING_ROLE",
] as const;
export type CheckInWorkType = (typeof CHECK_IN_WORK_TYPES)[number];

export const CHECK_IN_WORK_TYPE_LABELS: Record<CheckInWorkType, string> = {
  MORE_OF_THIS: "More of this project",
  RL_ENVIRONMENTS: "Building RL environments",
  AI_EVALUATION: "Reviewing AI output",
  EXPERT_TASKS: "Writing expert tasks & rubrics",
  CODING: "Coding & engineering",
  RED_TEAMING: "Red-teaming AI",
  DESIGN_CREATIVE: "Design & creative",
  VIDEO_RECORDING: "Recording video",
  ONGOING_ROLE: "An ongoing role on one team",
};

/** The monthly "would you recommend Terac" question's 0-10 scale. */
export const CHECK_IN_NPS_SCORES = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10] as const;

/**
 * An answer, whole or partial: the email's one tap sends only the Terac
 * rating, and the card fills in the rest later. Absent fields are left as
 * they are.
 */
export const checkInAnswerSchema = z.object({
  teracRating: z.number().int().min(1).max(5).optional(),
  projectRating: z.number().int().min(1).max(5).optional(),
  fulfillment: z.enum(CHECK_IN_FULFILLMENTS).optional(),
  outlook: z.enum(CHECK_IN_OUTLOOKS).optional(),
  wantMore: z.array(z.enum(CHECK_IN_WORK_TYPES)).max(9).optional(),
  wantMoreOther: z.string().trim().max(200).optional(),
  comment: z.string().trim().max(2000).optional(),
  npsScore: z.number().int().min(0).max(10).optional(),
  npsReason: z.string().trim().max(2000).optional(),
});
export type CheckInAnswer = z.infer<typeof checkInAnswerSchema>;

/**
 * An answer the dashboard puts under "Needs attention": a bad week with Terac
 * or on the project, someone winding down, or work that does not fulfil them.
 */
export function isAtRiskCheckIn(answer: {
  teracRating?: number | null;
  projectRating?: number | null;
  outlook?: string | null;
  fulfillment?: string | null;
}): boolean {
  return (
    (typeof answer.teracRating === "number" && answer.teracRating <= 2) ||
    (typeof answer.projectRating === "number" && answer.projectRating <= 2) ||
    answer.outlook === "STOPPING" ||
    answer.fulfillment === "NO"
  );
}

/** "Oct 19" for a `YYYY-MM-DD` Monday, read as a calendar day. */
export function formatCheckInWeek(weekOf: string): string {
  return new Date(`${weekOf}T00:00:00.000Z`).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}

/** Monday (UTC) of the week that holds `at`, as `YYYY-MM-DD`. */
export function checkInWeekOf(at: Date): string {
  const day = new Date(
    Date.UTC(at.getUTCFullYear(), at.getUTCMonth(), at.getUTCDate()),
  );
  const sinceMonday = (day.getUTCDay() + 6) % 7;
  day.setUTCDate(day.getUTCDate() - sinceMonday);
  return day.toISOString().slice(0, 10);
}

/** The zone an expert who never told us theirs is asked in. */
export const CHECK_IN_DEFAULT_TIME_ZONE = "America/Los_Angeles";

/**
 * The week an hourly sweep at `at` asks about, or null outside the window in
 * which some expert can be due. It opens at 02:00 UTC Friday, when UTC+14
 * reaches 4pm, and stays open to the end of Sunday UTC, so an expert whose 4pm
 * run was missed (a failed run, a deploy, a late roster) is still asked later
 * that weekend rather than skipped for the week.
 */
export function checkInSweepWeekOf(at: Date): string | null {
  const day = at.getUTCDay();
  const due = (day === 5 && at.getUTCHours() >= 2) || day === 6 || day === 0;
  return due ? checkInWeekOf(at) : null;
}

function isKnownTimeZone(timeZone: string): boolean {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone });
    return true;
  } catch {
    return false;
  }
}

/**
 * Whether an expert is due their check-in for `weekOf` at `at`: from 4pm on
 * that week's Friday in their zone (the default zone when theirs is missing or
 * unknown) onwards. 4pm is when they are first asked; any later run in the
 * sweep's window still asks them, and the check-in row and notification key
 * keep it to once.
 */
export function isCheckInDue(
  at: Date,
  weekOf: string,
  timeZone: string | null,
): boolean {
  const zone =
    timeZone && isKnownTimeZone(timeZone)
      ? timeZone
      : CHECK_IN_DEFAULT_TIME_ZONE;
  const friday = new Date(`${weekOf}T00:00:00.000Z`);
  friday.setUTCDate(friday.getUTCDate() + 4);
  const fridayDate = friday.toISOString().slice(0, 10);
  return (
    fromZonedTime(`${fridayDate}T16:00:00`, zone).getTime() <= at.getTime()
  );
}
